import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import { useEffect, type ReactNode } from "react";

import { NAV_BAR_HEIGHT, transientOptions, zIndexes } from "@skilitics/designer-ui-components";

import { collapseThumbnailNavigationBar } from "../../application/applicationSettingsAtoms";
import { useIsTriggersOpen, useSelectedResponseId, useSetSelectedResponseId } from "../../selection/useSelectedAspect";
import { responseIdExistsSelector, useNautilusSelector } from "../../state";
import { HandlerDesignerContextProvider } from "../HandlerDesignerContextProvider";
import { useTriggerDesigns } from "../TriggerDesignsContext";
import { ResponseEditorPage } from "./ResponseEditorPage";

const ResponseEditorOverlay = styled("div", transientOptions)<{ $thumbnailNavigationBarCollapsed?: boolean }>`
  position: absolute;
  z-index: ${zIndexes.triggersOverlay};
  inset: 0;
  background: ${({ theme }) => theme.surface};

  // note: keep some space for the thumbnail navigation bar
  height: ${({ $thumbnailNavigationBarCollapsed = false }) =>
    `calc(100% - ${$thumbnailNavigationBarCollapsed ? 0 : NAV_BAR_HEIGHT}px)`};
`;

interface Props {
  children?: ReactNode;
}

export const ResponseDetailsView = ({ children }: Props) => {
  const selectedResponseId = useSelectedResponseId();
  const setSelectedResponseId = useSetSelectedResponseId();
  const isTriggersOpen = useIsTriggersOpen();

  const responseIdExists = useNautilusSelector((state) =>
    responseIdExistsSelector(state, { responseId: selectedResponseId }),
  );

  useEffect(() => {
    if (selectedResponseId && (!isTriggersOpen || !responseIdExists)) {
      // reset selected response id (in location hash)
      // when triggers panel is closed
      // or if the selected response id doesn't exist anymore (e.g. it was deleted).
      setSelectedResponseId();
    }
  }, [isTriggersOpen, responseIdExists, selectedResponseId, setSelectedResponseId]);

  return (
    <>
      {children}
      {isTriggersOpen && selectedResponseId && responseIdExists && (
        <ResponsesOverlay selectedResponseId={selectedResponseId} />
      )}
    </>
  );
};

type ResponsesOverlayProps = {
  selectedResponseId: number;
};

const ResponsesOverlay = ({ selectedResponseId }: ResponsesOverlayProps) => {
  const { getHandlerId, getTriggerId } = useTriggerDesigns();
  const handlerId = getHandlerId(selectedResponseId);
  const triggerId = getTriggerId(selectedResponseId);

  const thumbnailNavigationBarCollapsed = useAtomValue(collapseThumbnailNavigationBar);

  return (
    <HandlerDesignerContextProvider handlerId={handlerId} triggerId={triggerId}>
      <ResponseEditorOverlay $thumbnailNavigationBarCollapsed={thumbnailNavigationBarCollapsed}>
        <ResponseEditorPage selectedResponseId={selectedResponseId} />
      </ResponseEditorOverlay>
    </HandlerDesignerContextProvider>
  );
};
